<script lang="ts" setup>
	// @ts-expect-error — o pacote não publica tipos.
	import { StreamBarcodeReader } from "@teckel/vue-barcode-reader";

	/**
	 * Leitura de código de barras pela câmera do aparelho.
	 *
	 * Existe para o operador em celular ou tablet, que não tem o laser do
	 * coletor. O código lido sobe por `scan-detected` e o `R3Input` o trata como
	 * se tivesse sido digitado e confirmado com Enter — a tela não distingue a
	 * origem.
	 *
	 * ## Requer contexto seguro
	 *
	 * `getUserMedia` só existe em HTTPS ou em `localhost`. Servido por HTTP num
	 * IP da rede, `navigator.mediaDevices` vem `undefined` e o navegador não dá
	 * erro visível — daí o aviso explícito em vez de uma tela preta.
	 */

	const visible = defineModel<boolean>("visible");

	const emits = defineEmits<{
		(e: "scan-detected", codigo: string): void;
	}>();

	/** Impede que o mesmo código dispare duas vezes antes de o diálogo fechar. */
	const lido = ref(false);
	const erro = ref("");
	const carregando = ref(true);

	const torchStatus = ref(false);
	const landscapeStatus = ref(false);

	/** Uma entrada por câmera traseira do aparelho, na ordem que o navegador expõe. */
	interface CameraDisponivel {
		deviceId: string;
		label: string;
	}

	const cameras = ref<CameraDisponivel[]>([]);

	/**
	 * Câmera em uso, por índice na lista.
	 *
	 * Começa em zero mas **não força nada**: o `StreamBarcodeReader` só troca de
	 * câmera quando esta prop *muda*, e na abertura ele já escolheu a dele —
	 * preferindo a que tem lanterna e foco contínuo, e lembrando a escolha no
	 * `localStorage`. O `@update:videoDevices` traz o índice real e sincroniza
	 * este valor, o que faz o seletor mostrar a câmera certa sem provocar troca.
	 */
	const cameraIndice = ref(0);

	/**
	 * Aparelho com mais de uma câmera traseira é a razão de existir o seletor.
	 *
	 * Celular moderno expõe grande-angular, teleobjetiva e macro como câmeras
	 * distintas, e a "ideal" que a biblioteca escolhe às vezes é a que não
	 * consegue focar de perto — justamente o que a leitura de etiqueta precisa.
	 */
	const podeEscolherCamera = computed(() => cameras.value.length > 1);

	/** A câmera só é acessível em contexto seguro. */
	const suportado = computed(
		() => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia,
	);

	watch(visible, (aberto) => {
		if (!aberto) {
			return;
		}

		lido.value = false;
		carregando.value = true;
		erro.value = suportado.value
			? ""
			: "A câmera só funciona em HTTPS ou localhost. Use o leitor do coletor "
				+ "ou digite o código.";
	});

	/**
	 * `@decode` do `StreamBarcodeReader` entrega o texto já decodificado.
	 *
	 * O diálogo fecha na primeira leitura: manter a câmera aberta faria o mesmo
	 * código ser lido em sequência enquanto a etiqueta estivesse no quadro.
	 */
	function onDecode(texto: string) {
		if (lido.value || !texto) {
			return;
		}

		lido.value = true;
		emits("scan-detected", texto);
		visible.value = false;
	}

	function onLoaded() {
		carregando.value = false;
	}

	/**
	 * Lista de câmeras, emitida quando o vídeo começa a rodar.
	 *
	 * Chega a cada troca de câmera, e é daqui que sai o índice em uso.
	 */
	function onVideoDevices(info: {
		devices?: { deviceId: string; label: string }[];
		selectedIndex?: number;
	}) {
		cameras.value = (info?.devices ?? []).map((d, i) => ({
			deviceId: d.deviceId,
			// Android costuma rotular "camera2 0, facing back"; sem rótulo algum,
			// numerar é melhor do que uma linha vazia no seletor.
			label: d.label?.trim() || `Câmera ${i + 1}`,
		}));

		if (typeof info?.selectedIndex === "number" && info.selectedIndex >= 0) {
			cameraIndice.value = info.selectedIndex;
		}
	}

	/** Trocar de câmera reabre o vídeo; o aviso evita a impressão de travamento. */
	function trocarCamera(indice: number) {
		if (indice === cameraIndice.value) {
			return;
		}

		carregando.value = true;
		cameraIndice.value = indice;
	}
</script>

<template>
	<Dialog
		v-model:visible="visible"
		modal
		header="Ler código de barras"
		class="w-full h-full justify-content-center"
	>
		<div class="flex flex-column w-full h-full gap-2">
			<p v-if="erro" class="scanner-erro">{{ erro }}</p>

			<template v-else>
				<div class="flex flex-row align-items-center justify-content-between gap-2">
					<label v-if="podeEscolherCamera" class="scanner-camera">
						<span class="scanner-camera-rotulo">Câmera</span>
						<select
							:value="cameraIndice"
							@change="trocarCamera(Number(($event.target as HTMLSelectElement).value))"
						>
							<option v-for="(cam, i) in cameras" :key="cam.deviceId" :value="i">
								{{ cam.label }}
							</option>
						</select>
					</label>

					<div v-else />

					<div class="flex flex-row gap-3">
						<Button rounded :outlined="!torchStatus" aria-label="Lanterna" @click="torchStatus = !torchStatus">
							<!-- SVG evita depender do carregamento da fonte de ícones no navegador do coletor. -->
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
								<path d="M9 18h6" />
								<path d="M10 22h4" />
								<path d="M15.09 14c.57-.93.91-2.03.91-3.2A4 4 0 0 0 8 10.8c0 1.17.34 2.27.91 3.2.43.7.81 1.32.9 2h4.38c.09-.68.47-1.3.9-2Z" />
							</svg>
						</Button>
						<Button rounded :outlined="!landscapeStatus" aria-label="Girar tela" @click="landscapeStatus = !landscapeStatus">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
								<path d="M3 7v4h4" />
								<path d="M21 17v-4h-4" />
								<path d="M20 11A8 8 0 0 0 6.3 5.3L3 7" />
								<path d="M4 13a8 8 0 0 0 13.7 5.7L21 17" />
							</svg>
						</Button>
					</div>
				</div>

				<p v-if="carregando" class="scanner-aviso">Abrindo a câmera…</p>

				<StreamBarcodeReader
					:torch="torchStatus"
					:landscape="landscapeStatus"
					:device-index="cameraIndice"
					no-front-cameras
					@decode="onDecode"
					@loaded="onLoaded"
					@update:video-devices="onVideoDevices"
				/>
			</template>
		</div>
	</Dialog>
</template>

<style scoped>
	.scanner-erro {
		margin: 0;
		color: #c0392b;
		font-size: 0.9rem;
	}

	.scanner-aviso {
		margin: 0;
		font-size: 0.85rem;
		opacity: 0.75;
	}

	.scanner-camera {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		min-width: 0;
	}

	.scanner-camera-rotulo {
		font-size: 0.8rem;
		font-weight: 600;
		white-space: nowrap;
	}

	/* Rótulo de câmera no Android é longo; truncar evita empurrar os botões. */
	.scanner-camera select {
		font: inherit;
		max-width: 12rem;
		padding: 0.35rem 0.4rem;
		border: 1px solid color-mix(in srgb, currentColor 25%, transparent);
		border-radius: 6px;
		background: transparent;
		color: inherit;
		text-overflow: ellipsis;
	}
</style>
